import { useEffect, useState } from 'react'
import { useAdmin } from '@vtex/raccoon-next'
import { type ImportError } from '@/components/products-listing/components/ImportAlerts/ImportAlerts'
import { useFeatureFlag } from '@/contexts/feature-flag'
import { useUserProfile } from '@/hooks/useUserProfile'
import { fetcher } from '@/lib/fetcher'

export interface ImportResponseProps {
  HashLink: string
  Type: string
  CreatedAt: string
  TotalRows: number
  Progress: number
  Status: string
  FileLink: string | null | undefined
}

export const useProductImport = () => {
  const { data: user } = useUserProfile(true)

  const [importResponse, setImportResponse] = useState<
    Record<string, ImportResponseProps>
  >({})
  const [importError, setImportError] = useState<ImportError>({})
  const [actualHash, setActualHash] = useState<string>('')

  useEffect(() => {
    if (
      actualHash !== '' &&
      importResponse[actualHash]?.Status !== 'Completed'
    ) {
      setTimeout(() => getImportRequest(actualHash), 2000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importResponse, actualHash])

  const getImportRequest = async (lastHash: string) => {
    setActualHash(lastHash)
    try {
      const response = await fetcher({
        url: `/api/proxy/catalog/pvt/import/${lastHash}`,
      })
      setImportResponse((prev) => ({
        ...prev,
        [lastHash]: response as ImportResponseProps,
      }))

      return response
    } catch (_) {
      setImportResponse({})
      return null
    }
  }

  const postImportRequest = async (type: string, file: File) => {
    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await fetcher({
        url: `/api/proxy/catalog/pvt/admin/product/import/${type}?&emailTo=${user?.email}`,
        method: 'POST',
        body: formData,
      })

      if (response?.HashLink) {
        setImportResponse((prev) => ({
          ...prev,
          [response.HashLink]: response as ImportResponseProps,
        }))
        setImportError({})
        await getImportRequest(response.HashLink)
      }
      return response
    } catch (error) {
      setActualHash('')
      setImportResponse({})

      // Extract the API response from the error if available
      const apiError = error as Error & { cause?: { info?: ImportError } }
      const importErrorData = apiError.cause?.info || {
        Message: apiError.message,
      }

      setImportError(importErrorData)
      return null
    }
  }

  return {
    postImportRequest,
    getImportRequest,
    importResponse,
    importError,
    actualHash,
  }
}

export const useImportTemplate = () => {
  const { locale, workspace, account, token } = useAdmin()
  const isUsingBetaAPIs = useFeatureFlag('use_beta_apis')

  const getImportTemplate = async (type: string) => {
    try {
      const url = `/api/proxy/catalog/pvt/admin/product/import/${type}/template?an=${account}`

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          token,
          account,
          workspace,
          ...(isUsingBetaAPIs && { 'vtex-commerce-env': 'beta' }),
          Accept: 'application/json',
          ...(locale && { 'accept-language': locale }),
        },
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const blob = await response.blob()

      const downloadUrl = URL.createObjectURL(blob)
      Object.assign(document.createElement('a'), {
        href: downloadUrl,
        download: `${type}-template.xlsx`,
      }).click()

      URL.revokeObjectURL(downloadUrl)

      return blob
    } catch (_) {
      return null
    }
  }

  return {
    getImportTemplate,
  }
}
